import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { currency } from "@/lib/constants";
import { Shield, AlertTriangle, FileWarning, CheckSquare, Flag, ChevronRight } from "lucide-react";

const CHECKLIST_LABELS = {
  w9: "W9", coi: "COI", sunbiz: "SunBiz", oig: "OIG", sam: "SAM", it_security: "IT Sec",
};

function Stat({ label, value, sub, icon: Icon, color, onClick, active }) {
  return (
    <button onClick={onClick} type="button"
            className={`text-left bg-white border rounded-md p-4 w-full transition ${active ? "border-[#005289] ring-2 ring-[#005289]/20" : "border-slate-200 hover:border-[#005289] hover:shadow-sm"}`}
            data-testid={`compliance-tile-${label.toLowerCase().replace(/[^\w]+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
          <div className="text-[10px] text-[#005289] font-semibold mt-1 flex items-center gap-0.5">Click to drill down <ChevronRight className="w-3 h-3" /></div>
        </div>
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: color + "15" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
    </button>
  );
}

export default function ComplianceDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("flags");

  useEffect(() => {
    api.get("/compliance/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-slate-500">Loading compliance dashboard...</div>;

  return (
    <div className="p-6 space-y-6" data-testid="compliance-page">
      <div>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#005289]" />
          <h1 className="text-2xl font-bold text-slate-900">Compliance Dashboard</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">Every open compliance flag, PHI/BAA contract, and OIG/SAM checklist exception across HCD.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Open Compliance Flags" value={data.counts.open_flags} icon={Flag} color="#EF4444" sub="Blocks execution" onClick={() => setTab("flags")} active={tab === "flags"} />
        <Stat label="PHI / BAA Contracts" value={data.counts.phi_baa} icon={Shield} color="#005289" sub="Data protection risk" onClick={() => setTab("phi")} active={tab === "phi"} />
        <Stat label="Checklist Gaps" value={data.counts.checklist_gaps} icon={CheckSquare} color="#F59E0B" sub="W9/COI/OIG/SAM/SunBiz" onClick={() => setTab("checklist")} active={tab === "checklist"} />
        <Stat label="High-Severity Red Flags" value={data.counts.high_red} icon={AlertTriangle} color="#7F1D1D" sub="Blocks approval" onClick={() => setTab("high")} active={tab === "high"} />
      </div>

      <div className="border-b border-slate-200 flex gap-4">
        {[
          { k: "flags", label: `Open Compliance Flags (${data.counts.open_flags})`, icon: Flag },
          { k: "phi", label: `PHI / BAA (${data.counts.phi_baa})`, icon: Shield },
          { k: "checklist", label: `Checklist Gaps (${data.counts.checklist_gaps})`, icon: CheckSquare },
          { k: "high", label: `High-Severity (${data.counts.high_red})`, icon: AlertTriangle },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-1 py-3 text-xs font-semibold border-b-2 flex items-center gap-1.5 ${tab === t.k ? "border-[#005289] text-[#005289]" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                  data-testid={`compliance-tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {tab === "flags" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="hcd-table">
            <thead><tr><th>Contract #</th><th>Vendor</th><th>Department</th><th>Note</th><th>Raised By</th></tr></thead>
            <tbody>
              {data.open_compliance_flags.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">No open compliance flags. Great work.</td></tr>
              )}
              {data.open_compliance_flags.map(f => (
                <tr key={f.id} onClick={() => nav(`/contract/${f.contract_id}`)} className="cursor-pointer">
                  <td className="font-mono text-xs">{f.contract_number}</td>
                  <td className="font-semibold">{f.vendor}</td>
                  <td className="text-slate-600">{f.department}</td>
                  <td className="text-slate-700 max-w-md truncate">{f.note}</td>
                  <td className="text-xs text-slate-500">{f.raised_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "phi" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="hcd-table">
            <thead><tr><th>Contract #</th><th>Vendor</th><th>Department</th><th>Stage</th><th>PHI Flag</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {data.phi_baa_contracts.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500 text-sm">No PHI/BAA contracts on file.</td></tr>
              )}
              {data.phi_baa_contracts.map(c => (
                <tr key={c.id} onClick={() => nav(`/contract/${c.id}`)} className="cursor-pointer" data-testid={`phi-${c.contract_number}`}>
                  <td className="font-mono text-xs">{c.contract_number}</td>
                  <td className="font-semibold">{c.vendor}</td>
                  <td className="text-slate-600">{c.department}</td>
                  <td>{c.stage.replace(/_/g, " ")}</td>
                  <td>
                    {c.has_phi_flag ? (
                      <span className={`chip ${c.phi_flag_severity === "High" ? "chip-overdue" : "chip-duesoon"}`}>
                        <span className="dot" />BAA {c.phi_flag_severity}
                      </span>
                    ) : <span className="chip chip-draft"><span className="dot" />PHI Yes</span>}
                  </td>
                  <td className="num">{currency(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "checklist" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="hcd-table">
            <thead><tr><th>Contract #</th><th>Vendor</th><th>Department</th><th>Stage</th><th>Missing Items</th></tr></thead>
            <tbody>
              {data.checklist_gaps.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">No checklist gaps. All vendor screenings up to date.</td></tr>
              )}
              {data.checklist_gaps.map(c => (
                <tr key={c.id} onClick={() => nav(`/contract/${c.id}`)} className="cursor-pointer">
                  <td className="font-mono text-xs">{c.contract_number}</td>
                  <td className="font-semibold">{c.vendor}</td>
                  <td className="text-slate-600">{c.department}</td>
                  <td>{c.stage.replace(/_/g, " ")}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {c.missing.map(k => (
                        <span key={k} className="chip chip-duesoon"><span className="dot" />{CHECKLIST_LABELS[k] || k}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "high" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="hcd-table">
            <thead><tr><th>Contract #</th><th>Vendor</th><th>Department</th><th>Flag Type</th><th>Notes</th></tr></thead>
            <tbody>
              {data.high_severity_red_flags.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">No open High-severity red flags.</td></tr>
              )}
              {data.high_severity_red_flags.map((r, i) => (
                <tr key={i} onClick={() => nav(`/contract/${r.id}`)} className="cursor-pointer">
                  <td className="font-mono text-xs">{r.contract_number}</td>
                  <td className="font-semibold">{r.vendor}</td>
                  <td className="text-slate-600">{r.department}</td>
                  <td className="font-semibold text-red-700">{r.flag_type}</td>
                  <td className="text-slate-700 max-w-md truncate">{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
